const asArray = value => Array.isArray(value) ? value : Object.values(value || {}).filter(Boolean);
const num = value => value == null || value === "" ? null : Number(value);
const firstName = value => String(value || "").trim().split(/\s+/)[0] || "";

function userById(users, id) {
  const numericId = num(id);
  return numericId == null ? null : users.find(user => Number(user.id) === numericId) || null;
}

function nameFor(users, id, fallback) {
  return firstName(userById(users, id)?.name || fallback || "");
}

function validTimestamp(value) {
  return value && !Number.isNaN(Date.parse(value)) ? value : null;
}

function classifyManualTransfer({ fromId, toId, toAurora, actorRole, crossStore }) {
  if (crossStore) return "cross_store";
  if (toAurora) return "consultant_to_aurora";
  if (actorRole === "manager" || actorRole === "admin") return "manager_to_consultant";
  if (fromId != null && toId != null) return "consultant_to_consultant";
  return "consultant_to_consultant";
}

function eventKey(event) {
  return [
    event.timestamp || "",
    event.fromId ?? event.fromLabel,
    event.toId ?? event.toLabel,
    event.type,
  ].join("|");
}

export function transferEventsForLead(lead, recordedEvents = {}, users = []) {
  const projected = [];

  if (lead?.aurora_handoff === true && lead.aurora_handoff_event_id && validTimestamp(lead.aurora_handoff_at)) {
    const toId = num(lead.aurora_handoff_consultant_id);
    projected.push({
      id: lead.aurora_handoff_event_id,
      type: "aurora_to_consultant",
      timestamp: lead.aurora_handoff_at,
      fromId: null,
      toId,
      fromLabel: "Aurora",
      toLabel: nameFor(users, toId, lead.aurora_handoff_consultant_name) || "Consultor",
      actorId: null,
      actorLabel: "Aurora",
      actorRole: "aurora",
      reason: lead.aurora_handoff_reason || null,
      crossStore: false,
      source: "aurora_handoff",
    });
  }

  asArray(lead?.historicoTransferencias).forEach((item, index) => {
    if (!item || item.origem !== "manual_transfer" || !validTimestamp(item.data)) return;
    const fromId = num(item.de);
    const toId = num(item.para);
    const actorId = num(item.transferidoPor);
    const actor = userById(users, actorId);
    const fromFallback = item.deNome || item.origemNome || item.consultorAnteriorNome;
    const toFallback = item.paraNome || item.destinoNome || item.consultorDestinoNome;
    const toAurora = String(item.para || item.destino || "").toLowerCase() === "aurora";
    const sourceStore = num(item.lojaAnterior);
    const destinationStore = num(item.lojaDestino);
    const crossStore = sourceStore != null && destinationStore != null && sourceStore !== destinationStore;
    projected.push({
      id: item.id || `manual_${index}_${item.data}`,
      type: classifyManualTransfer({ fromId, toId, toAurora, actorRole:actor?.role, crossStore }),
      timestamp: item.data,
      fromId,
      toId: toAurora ? null : toId,
      fromLabel: nameFor(users, fromId, fromFallback) || "Consultor",
      toLabel: toAurora ? "Aurora" : nameFor(users, toId, toFallback) || "Consultor",
      actorId,
      actorLabel: nameFor(users, actorId, item.transferidoPorNome) || null,
      actorRole: actor?.role || null,
      reason: item.motivo || item.reason || null,
      crossStore,
      sourceStore,
      destinationStore,
      source: "historico_transferencias",
    });
  });

  asArray(recordedEvents).forEach((item, index) => {
    if (!item || !["transferencia", "devolvido_aurora"].includes(item.tipo) || !validTimestamp(item.timestamp)) return;
    const toAurora = item.tipo === "devolvido_aurora" || String(item.para || "").toLowerCase() === "aurora";
    const fromId = num(item.deId ?? item.consultorAnteriorId);
    const toId = num(item.paraId ?? item.consultorDestinoId);
    projected.push({
      id: item.id || `event_${index}_${item.timestamp}`,
      type: toAurora ? "consultant_to_aurora" : "consultant_to_consultant",
      timestamp: item.timestamp,
      fromId,
      toId: toAurora ? null : toId,
      fromLabel: nameFor(users, fromId, item.de || item.consultorAnteriorNome) || "Consultor",
      toLabel: toAurora ? "Aurora" : nameFor(users, toId, item.para) || "Consultor",
      actorId: num(item.executadoPorId),
      actorLabel: item.executadoPor || null,
      actorRole: item.executadoPorRole || null,
      reason: item.motivo || null,
      crossStore: false,
      source: "lead_event",
    });
  });

  const seen = new Set();
  return projected
    .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
    .filter(event => {
      const key = eventKey(event);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function latestTransferEvent(events) {
  return events.length ? events[events.length - 1] : null;
}

export function transferEventMatches(event, filters = {}) {
  if (!event) return false;
  if (filters.type && filters.type !== "all" && event.type !== filters.type) return false;
  if (filters.aurora === "yes" && !["aurora_to_consultant", "consultant_to_aurora"].includes(event.type)) return false;
  if (filters.aurora === "no" && ["aurora_to_consultant", "consultant_to_aurora"].includes(event.type)) return false;
  if (filters.sourceConsultantId && Number(event.fromId) !== Number(filters.sourceConsultantId)) return false;
  if (filters.destinationConsultantId && Number(event.toId) !== Number(filters.destinationConsultantId)) return false;
  if (filters.storeId) {
    const storeId = Number(filters.storeId);
    if (![event.sourceStore, event.destinationStore].some(id => Number(id) === storeId)) return false;
  }
  if (filters.consultantId) {
    const consultantId = Number(filters.consultantId);
    if (![event.fromId, event.toId, event.actorId].some(id => Number(id) === consultantId)) return false;
  }
  const eventTime = Date.parse(event.timestamp);
  if (filters.from && eventTime < Date.parse(`${filters.from}T00:00:00-03:00`)) return false;
  if (filters.to && eventTime > Date.parse(`${filters.to}T23:59:59.999-03:00`)) return false;
  return true;
}

export function leadMatchesTransferFilters(events, filters = {}) {
  return events.some(event => transferEventMatches(event, filters));
}

export function transferEventText(event) {
  if (!event) return "";
  const icon = event.type === "aurora_to_consultant" ? "🤖" : "↔";
  return `${icon} ${event.fromLabel} transferiu para ${event.toLabel}`;
}

function timestampMs(value) {
  const raw = String(value ?? "").trim();
  if (/^\d{10,16}$/.test(raw)) {
    const numeric = Number(raw);
    return raw.length === 10 ? numeric * 1000 : raw.length > 13 ? Math.floor(numeric / 1000) : numeric;
  }
  return Date.parse(raw);
}

function isAcceptedHumanOutbound(message) {
  if (!message || message.remetente !== "consultor") return false;
  if ((message.messageSource || message.source) !== "crm") return false;
  return ["sent", "delivered", "read"].includes(String(message.status || "").toLowerCase());
}

export function transferResponseCycles(events = [], messages = []) {
  const orderedEvents = [...events].sort((a, b) => timestampMs(a.timestamp) - timestampMs(b.timestamp));
  const orderedMessages = asArray(messages)
    .filter(message => Number.isFinite(timestampMs(message?.timestamp || message?.createdAt)))
    .sort((a, b) => timestampMs(a.timestamp || a.createdAt) - timestampMs(b.timestamp || b.createdAt));

  return orderedEvents.map((event, index) => {
    const startedAt = timestampMs(event.timestamp);
    const nextStartedAt = index + 1 < orderedEvents.length ? timestampMs(orderedEvents[index + 1].timestamp) : Infinity;
    const destinationIsConsultant = event.toLabel !== "Aurora";
    if (!destinationIsConsultant) return { event, status:"not_applicable", response:null, elapsedMinutes:null };

    const cycleOutbound = orderedMessages.filter(message => {
      const messageTime = timestampMs(message.timestamp || message.createdAt);
      return messageTime > startedAt && messageTime < nextStartedAt && isAcceptedHumanOutbound(message);
    });
    const confirmed = cycleOutbound.find(message => (
      event.toId != null
      && message.consultantId != null
      && Number(message.consultantId) === Number(event.toId)
    ));
    if (confirmed) {
      return {
        event,
        status:"confirmed",
        response:confirmed,
        elapsedMinutes:Math.max(0, Math.round((timestampMs(confirmed.timestamp || confirmed.createdAt) - startedAt) / 60000)),
      };
    }
    return {
      event,
      status:cycleOutbound.length ? "unconfirmed" : "awaiting",
      response:null,
      elapsedMinutes:null,
    };
  });
}

export function formatTransferClock(timestamp) {
  const date = new Date(timestampMs(timestamp));
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString("pt-BR", { hour:"2-digit", minute:"2-digit", hour12:false, timeZone:"America/Sao_Paulo" });
}
